// src/api/features/suppliers/queries.ts
import {
  useQuery,
  type QueryKey,
  type QueryClient,
  keepPreviousData,
} from "@tanstack/react-query";

import {
  listSuppliers,
  getSupplier,
} from "./api";

import type {
  SupplierWithRelations,
  PaginatedSuppliers,
  ListSuppliersQuery,
} from "./types";

/* =========================
 * Query Keys
 * ========================= */

export const SuppliersKeys = {
  all: ["suppliers"] as const,
  list: (params: ListSuppliersQuery = {}) =>
    ["suppliers", "list", params] as const,
  // NOTE: detail is now the DEEP supplier (with products + contacts)
  detail: (id: number | string) =>
    ["suppliers", "detail", String(id)] as const,
} as const;

/* =========================
 * SUPPLIERS — React Query Hooks
 * ========================= */

/** 🔹 List suppliers with pagination + filters (base rows) */
export function useSuppliers(params: ListSuppliersQuery = {}) {
  const key = SuppliersKeys.list(params);

  return useQuery<PaginatedSuppliers>({
    queryKey: key,
    queryFn: () => listSuppliers(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * 🔹 Get a single supplier (DEEP)
 * - Returns SupplierWithRelations (base + products + contacts)
 */
export function useSupplier(id: number | string | null | undefined) {
  const enabled = id !== null && id !== undefined && id !== "";
  const detailKey: QueryKey = enabled
    ? SuppliersKeys.detail(id as number | string)
    : (["suppliers", "detail", "disabled"] as QueryKey);

  return useQuery<SupplierWithRelations>({
    queryKey: detailKey,
    queryFn: () => getSupplier(id as number | string),
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/* =========================
 * Prefetch helpers (optional)
 * ========================= */

export function prefetchSuppliers(
  qc: QueryClient,
  params: ListSuppliersQuery = {}
) {
  return qc.prefetchQuery({
    queryKey: SuppliersKeys.list(params),
    queryFn: () => listSuppliers(params),
    staleTime: 30_000,
  });
}

export function prefetchSupplier(qc: QueryClient, id: number | string) {
  return qc.prefetchQuery({
    queryKey: SuppliersKeys.detail(id),
    queryFn: () => getSupplier(id),
    staleTime: 60_000,
  });
}
